import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, MousePointer, Reply } from 'lucide-react';
import React from 'react';
import { inboxKeys } from './useUnifiedInbox';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * Connects to the SSE tracking endpoint and:
 * 1. Shows toast notifications for open/click/reply events
 * 2. Invalidates React Query cache so tracking data refreshes
 * 3. Listens for `inbox_message` events (emitted by the ingest pipeline)
 *    and invalidates unified-inbox caches for real-time message updates.
 *
 * This is the SINGLE SSE connection for the inbox page — the backend
 * multiplexes all event types (tracking + inbox) onto one per-user stream.
 * Polling (60s) remains as the fallback if the connection drops.
 */
export function useTrackingSSE() {
    const { token } = useAuth();
    const queryClient = useQueryClient();
    // Some test harnesses return a fresh facade on every render. Keep the
    // client captured for this hook lifecycle so a harmless rerender does not
    // exchange another one-shot SSE ticket or open a second stream.
    const queryClientRef = useRef(queryClient);
    const stableQueryClient = queryClientRef.current;
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const connectingRef = useRef(false);
    const lifecycleRef = useRef(0);
    const connectRef = useRef(null);

    const connect = useCallback(async () => {
        if (!token || eventSourceRef.current || connectingRef.current) return;

        const lifecycle = lifecycleRef.current;
        connectingRef.current = true;

        const scheduleReconnect = () => {
            if (
                lifecycle !== lifecycleRef.current ||
                reconnectTimeoutRef.current
            ) return;
            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectTimeoutRef.current = null;
                if (lifecycle === lifecycleRef.current) connectRef.current?.();
            }, 5000);
        };

        // Fetch a short-lived ticket (NOT the raw JWT) before opening the
        // browser-managed EventSource connection. EventSource cannot send the
        // session Authorization header, so the ticket is the only URL value.
        let sseToken;
        try {
            const res = await fetch(`${API}/sse/token`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) {
                console.warn('SSE token fetch failed, retrying in 5s');
                scheduleReconnect();
            } else {
                const data = await res.json();
                sseToken = data.sse_token;
                if (!sseToken) {
                    console.warn('SSE token missing in response, retrying in 5s');
                    scheduleReconnect();
                }
            }
        } catch (err) {
            console.warn('SSE token fetch error, retrying in 5s:', err);
            scheduleReconnect();
        } finally {
            if (lifecycle === lifecycleRef.current) connectingRef.current = false;
        }

        // The ticket fetch can finish after the surface has unmounted or after
        // a newer token lifecycle started. Do not create an orphaned stream.
        if (!sseToken || lifecycle !== lifecycleRef.current) return;

        const url = `${API}/sse/tracking?token=${encodeURIComponent(sseToken)}`;
        const es = new EventSource(url);
        eventSourceRef.current = es;

        es.onopen = () => {
            if (lifecycle !== lifecycleRef.current) return;
            // Connected successfully — clear any reconnect timer.
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        es.onerror = () => {
            if (lifecycle !== lifecycleRef.current) return;
            // Close and schedule reconnect. Guard against stacked timeouts:
            // a dropped connection is expected during dev hot-reloads and
            // network blips; polling covers the gap.
            es.close();
            eventSourceRef.current = null;
            scheduleReconnect();
        };

        // Email opened (SSE event name: "email_open" to avoid collision with EventSource built-in "open")
        es.addEventListener('email_open', (e) => {
            try {
                const data = JSON.parse(e.data);
                toast(
                    React.createElement('div', { className: 'flex items-center gap-2' },
                        React.createElement(Eye, { className: 'w-4 h-4 text-violet-600 flex-shrink-0' }),
                        React.createElement('span', null,
                            React.createElement('strong', null, data.to_email),
                            ' opened: ',
                            data.subject || 'your email'
                        )
                    ),
                    { duration: 5000 }
                );
                stableQueryClient.invalidateQueries({ queryKey: ['tracking'] });
            } catch { /* ignore parse errors */ }
        });

        // Link clicked
        es.addEventListener('email_click', (e) => {
            try {
                const data = JSON.parse(e.data);
                toast(
                    React.createElement('div', { className: 'flex items-center gap-2' },
                        React.createElement(MousePointer, { className: 'w-4 h-4 text-blue-600 flex-shrink-0' }),
                        React.createElement('span', null,
                            React.createElement('strong', null, data.to_email),
                            ' clicked a link in: ',
                            data.subject || 'your email'
                        )
                    ),
                    { duration: 5000 }
                );
                stableQueryClient.invalidateQueries({ queryKey: ['tracking'] });
            } catch { /* ignore */ }
        });

        // Reply detected
        es.addEventListener('email_reply', (e) => {
            try {
                const data = JSON.parse(e.data);
                toast(
                    React.createElement('div', { className: 'flex items-center gap-2' },
                        React.createElement(Reply, { className: 'w-4 h-4 text-green-600 flex-shrink-0' }),
                        React.createElement('span', null,
                            React.createElement('strong', null, data.to_email),
                            ' replied to: ',
                            data.subject || 'your email'
                        )
                    ),
                    { duration: 5000 }
                );
                stableQueryClient.invalidateQueries({ queryKey: ['tracking'] });
                stableQueryClient.invalidateQueries({ queryKey: ['gmail'] });
            } catch { /* ignore */ }
        });

        // Server ingestion publishes one event for either lane. Invalidate
        // both roots so a pooled and personal copy remain fresh even when the
        // payload's scope is absent or the other surface is currently active.
        es.addEventListener('inbox_message', () => {
            stableQueryClient.invalidateQueries({ queryKey: ['inbox'] });
            stableQueryClient.invalidateQueries({ queryKey: inboxKeys.unrepliedCounts });
            stableQueryClient.invalidateQueries({ queryKey: ['personal-inbox'] });
        });
    }, [token, stableQueryClient]);

    connectRef.current = connect;

    useEffect(() => {
        lifecycleRef.current += 1;
        connect();
        return () => {
            lifecycleRef.current += 1;
            connectingRef.current = false;
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [connect]);
}
